import React, { Suspense, lazy, memo, forwardRef } from 'react';
import { type Icon, type IconProps } from '@phosphor-icons/react';

interface PhosphorIconProps extends IconProps {
  iconName: string;
}

/**
 * A flexible icon component that supports both Phosphor icons and image-based icons.
 *
 * The component uses dynamic imports to lazy load Phosphor icons, reducing the initial
 * bundle size by only loading icons when they're needed.
 *
 * @component
 * @example
 * // Using a Phosphor icon
 * <Icon iconName="House" size={24} />
 *
 * // Using an image icon
 * <Icon iconPath="/path/to/icon.svg" />
 */

// Cache lazy components by icon name so React sees a stable component reference
// across renders. Calling lazy() inside a render function creates a new component
// identity every render, causing remounts and repeated Suspense fallback flashes.
const lazyIconCache = new Map<string, ReturnType<typeof lazy>>();

function getLazyIcon(iconName: string) {
  if (!lazyIconCache.has(iconName)) {
    lazyIconCache.set(
      iconName,
      lazy(() =>
        import('@phosphor-icons/react').then((module) => {
          const icons = module as unknown as Record<string, Icon>;
          return { default: icons[iconName] };
        }),
      ),
    );
  }
  return lazyIconCache.get(iconName)!;
}

const PhosphorIcon = forwardRef<SVGSVGElement, PhosphorIconProps>(({ iconName, ...props }, ref) => {
  const IconComponent = getLazyIcon(iconName);

  return (
    <Suspense fallback={<></>}>
      <IconComponent {...props} ref={ref} />
    </Suspense>
  );
});

PhosphorIcon.displayName = 'PhosphorIcon';

const Icon = ({ iconPath }: { iconPath: string }) => {
  return <img src={iconPath} alt="icon" />;
};

// Export a memoized version of the PhosphorIcon component
export const MemoizedIcon = memo(PhosphorIcon);
export { PhosphorIcon as Icon };
